import { Database, Tables } from '@dolfin/business/db_types'
import { useSupabaseClient } from '@dolfin/hooks/supabase'
import { useCallback, useEffect, useMemo } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { QueryKeys } from './queryKeys'
import { useOrgContext } from './state/organization'

export type IssueUnreadCountsByStage = Record<'open' | 'in_review' | 'resolved', number>

interface UnreadIssueActivityData {
  unreadIssueIds: number[]
  unreadIssueCount: number
  unreadCountsByStage: IssueUnreadCountsByStage
  notificationIdsByIssue: Record<number, number[]>
}

interface UnreadIssueNotificationRow {
  id: number
  entity_id: number | null
}

type UnreadIssueRow = Pick<Tables<'issues'>, 'id' | 'status' | 'resolved_at'>

const EMPTY_UNREAD_COUNTS: IssueUnreadCountsByStage = {
  open: 0,
  in_review: 0,
  resolved: 0
}

const EMPTY_UNREAD_ACTIVITY: UnreadIssueActivityData = {
  unreadIssueIds: [],
  unreadIssueCount: 0,
  unreadCountsByStage: EMPTY_UNREAD_COUNTS,
  notificationIdsByIssue: {}
}

function getIssueStage(
  issue: Pick<Tables<'issues'>, 'status' | 'resolved_at'>
): keyof IssueUnreadCountsByStage {
  if (issue.status === 'in_review' || issue.status === 'resolved') {
    return issue.status
  }

  return issue.resolved_at ? 'resolved' : 'open'
}

export function buildUnreadIssueActivityData(
  unreadNotifications: UnreadIssueNotificationRow[] = [],
  unreadIssues: UnreadIssueRow[] = []
): UnreadIssueActivityData {
  const notificationIdsByIssue: Record<number, number[]> = {}

  unreadNotifications.forEach(notification => {
    if (notification.entity_id == null) return

    if (!notificationIdsByIssue[notification.entity_id]) {
      notificationIdsByIssue[notification.entity_id] = []
    }

    notificationIdsByIssue[notification.entity_id]?.push(notification.id)
  })

  const resolvedUnreadIssueIds = unreadIssues.map(issue => issue.id)
  const resolvedUnreadIssueIdSet = new Set(resolvedUnreadIssueIds)

  const unreadCountsByStage = unreadIssues.reduce<IssueUnreadCountsByStage>(
    (counts, issue) => {
      const stage = getIssueStage(issue)
      counts[stage] += 1
      return counts
    },
    { ...EMPTY_UNREAD_COUNTS }
  )

  return {
    unreadIssueIds: resolvedUnreadIssueIds,
    unreadIssueCount: resolvedUnreadIssueIds.length,
    unreadCountsByStage,
    notificationIdsByIssue: Object.fromEntries(
      Object.entries(notificationIdsByIssue).filter(([issueId]) =>
        resolvedUnreadIssueIdSet.has(Number(issueId))
      )
    )
  }
}

export function useIssueUnreadActivity() {
  const supabase = useSupabaseClient<Database>()
  const queryClient = useQueryClient()
  const organizationId = useOrgContext(state => state.selectedOrganization?.id)
  const currentUserId = useOrgContext(state => state.profile?.id)

  const queryKey = useMemo(
    () =>
      [
        QueryKeys.notifications.get,
        'issue-unread-activity',
        organizationId,
        currentUserId
      ] as const,
    [organizationId, currentUserId]
  )

  const fetchUnreadIssueActivity = useCallback(async (): Promise<UnreadIssueActivityData> => {
    if (!organizationId || !currentUserId) {
      return EMPTY_UNREAD_ACTIVITY
    }

    const { data: unreadNotifications, error: unreadNotificationsError } = await supabase
      .from('notifications')
      .select('id, entity_id, notifications_status!inner(status)')
      .eq('organization_id', organizationId)
      .eq('entity_type', 'issue')
      .eq('notifications_status.profile_id', currentUserId)
      .eq('notifications_status.status', 'unread')

    if (unreadNotificationsError) {
      throw unreadNotificationsError
    }

    const unreadIssueIds = [
      ...new Set(
        unreadNotifications
          ?.map(notification => notification.entity_id)
          .filter((issueId): issueId is number => issueId != null) ?? []
      )
    ]

    if (unreadIssueIds.length === 0) {
      return EMPTY_UNREAD_ACTIVITY
    }

    const { data: unreadIssues, error: unreadIssuesError } = await supabase
      .from('issues')
      .select('id, status, resolved_at')
      .eq('organization_id', organizationId)
      .in('id', unreadIssueIds)

    if (unreadIssuesError) {
      throw unreadIssuesError
    }

    return buildUnreadIssueActivityData(unreadNotifications ?? [], unreadIssues)
  }, [currentUserId, organizationId, supabase])

  const { data, isLoading } = useQuery(queryKey, fetchUnreadIssueActivity, {
    enabled: !!organizationId && !!currentUserId
  })

  useEffect(() => {
    if (!organizationId || !currentUserId) return

    const invalidateUnreadActivity = () => {
      queryClient.invalidateQueries(queryKey)
    }

    // Unread state only changes when the current user's notification status
    // changes or when an issue in the current org moves stage / disappears.
    const notificationsStatusChannel = supabase
      .channel(`issue-unread-notifications-status-${organizationId}-${currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications_status',
          filter: `profile_id=eq.${currentUserId}`
        },
        invalidateUnreadActivity
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications_status',
          filter: `profile_id=eq.${currentUserId}`
        },
        invalidateUnreadActivity
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications_status',
          filter: `profile_id=eq.${currentUserId}`
        },
        invalidateUnreadActivity
      )
      .subscribe()

    const issuesChannel = supabase
      .channel(`issue-unread-issues-${organizationId}-${currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'issues',
          filter: `organization_id=eq.${organizationId}`
        },
        invalidateUnreadActivity
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'issues',
          filter: `organization_id=eq.${organizationId}`
        },
        invalidateUnreadActivity
      )
      .subscribe()

    const channels = [notificationsStatusChannel, issuesChannel]

    return () => {
      channels.forEach(channel => {
        supabase.removeChannel(channel)
      })
    }
  }, [currentUserId, organizationId, queryClient, queryKey, supabase])

  const unreadIssueIds = useMemo(() => new Set(data?.unreadIssueIds ?? []), [data?.unreadIssueIds])

  const getIssueNotificationIds = useCallback(
    async (issueId: number) => {
      if (!organizationId || !currentUserId) {
        return []
      }

      const cachedNotificationIds = data?.notificationIdsByIssue[issueId] ?? []

      if (cachedNotificationIds.length > 0) {
        return cachedNotificationIds
      }

      const { data: issueNotifications, error } = await supabase
        .from('notifications')
        .select('id, notifications_status!inner(profile_id)')
        .eq('organization_id', organizationId)
        .eq('entity_type', 'issue')
        .eq('entity_id', issueId)
        .eq('notifications_status.profile_id', currentUserId)

      if (error) {
        throw error
      }

      return issueNotifications?.map(notification => notification.id) ?? []
    },
    [currentUserId, data?.notificationIdsByIssue, organizationId, supabase]
  )

  const updateIssueActivityStatus = useCallback(
    async (issueId: number, status: 'read' | 'unread') => {
      if (!currentUserId) return

      const notificationIds = await getIssueNotificationIds(issueId)

      if (!notificationIds.length) return

      const { error } = await supabase
        .from('notifications_status')
        .update({ status })
        .eq('profile_id', currentUserId)
        .in('notification_id', notificationIds)

      if (error) {
        throw error
      }

      await Promise.all([
        queryClient.invalidateQueries(queryKey),
        queryClient.invalidateQueries(QueryKeys.notifications.get)
      ])
    },
    [currentUserId, getIssueNotificationIds, queryClient, queryKey, supabase]
  )

  const markIssueActivityAsRead = useCallback(
    async (issueId: number) => updateIssueActivityStatus(issueId, 'read'),
    [updateIssueActivityStatus]
  )

  const markIssueActivityAsUnread = useCallback(
    async (issueId: number) => updateIssueActivityStatus(issueId, 'unread'),
    [updateIssueActivityStatus]
  )

  return {
    unreadIssueIds,
    unreadIssueCount: data?.unreadIssueCount ?? 0,
    unreadCountsByStage: data?.unreadCountsByStage ?? EMPTY_UNREAD_COUNTS,
    markIssueActivityAsRead,
    markIssueActivityAsUnread,
    isLoading
  }
}
